import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Slide, { type SlideProps } from "@mui/material/Slide";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { AdvisorAvatar } from "src/advisor/AdvisorAvatar";
import { AdvisorComposer } from "src/advisor/AdvisorComposer";
import { AdvisorConversation } from "src/advisor/AdvisorConversation";
import { useAdvisor } from "src/advisor/AdvisorProvider";

/** The view slides up from the tab bar, like a sheet. */
const SlideUp: FC<SlideProps> = props => <Slide {...props} direction="up" />;

const SAFE_AREA_TOP = "env(safe-area-inset-top, 0px)";
const TITLE_SEPARATOR = " · ";

/** What the user is looking at, from `document.title` ("<page> · Solution Advisor"). */
const currentPageTitle = (appTitle: string) => {
	if (!document.title.endsWith(`${TITLE_SEPARATOR}${appTitle}`)) return undefined;
	return document.title.slice(0, -`${TITLE_SEPARATOR}${appTitle}`.length) || undefined;
};

/**
 * Mobile advisor (Figma M12 Ask the Advisor): a full-screen view that slides up over the page, with
 * a back button, the advisor identity and the current page as context, and a one-line composer.
 */
export const AdvisorScreen: FC<{ open: boolean }> = ({ open }) => {
	const { t } = useTranslation();
	const { chat, closeAdvisor } = useAdvisor();
	const { pathname } = useLocation();
	const appTitle = t("app.title");
	const [context, setContext] = useState<string>();

	// Read the title after the page has set it (a render-time read would be memoized by the compiler).
	useEffect(() => {
		if (open) setContext(currentPageTitle(appTitle));
	}, [appTitle, open, pathname]);

	return (
		<Dialog
			fullScreen
			open={open}
			onClose={closeAdvisor}
			slots={{ transition: SlideUp }}
			slotProps={{ paper: { "aria-label": t("advisor.title") } }}>
			<Stack sx={{ height: "100%" }}>
				<Stack
					direction="row"
					spacing={1.25}
					sx={{
						alignItems: "center",
						px: 2,
						pt: SAFE_AREA_TOP,
						minHeight: `calc(52px + ${SAFE_AREA_TOP})`,
						borderBottom: 1,
						borderColor: "divider",
						flexShrink: 0
					}}>
					<IconButton onClick={closeAdvisor} aria-label={t("common.back")} sx={{ width: 44, height: 44, ml: -1.5 }}>
						<ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
					</IconButton>
					<AdvisorAvatar size={30} />
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography sx={{ fontSize: 16, fontWeight: 600 }}>{t("app.title")}</Typography>
						{context && (
							<Typography variant="caption" color="text.secondary" component="p" noWrap>
								{context}
							</Typography>
						)}
					</Box>
					<IconButton onClick={chat.reset} aria-label={t("advisor.newConversation")}>
						<AddIcon />
					</IconButton>
				</Stack>
				<AdvisorConversation chat={chat} />
				<AdvisorComposer variant="pill" busy={chat.busy} onSend={chat.send} />
			</Stack>
		</Dialog>
	);
};
